<template>
  <div class="audio-recorder">
    <div v-if="!hasAudio && !isRecording" class="mb-2">
      <v-btn
        color="primary"
        prepend-icon="mdi-microphone"
        @click="startRecording"
        :disabled="!canRecord"
      >
        Start Recording
      </v-btn>
      <div v-if="!canRecord" class="text-caption text-grey mt-1">
        Audio recording not supported in this browser
      </div>
    </div>

    <div v-if="isRecording" class="recording-controls mb-2 pa-2" style="background-color: #ffebee; border-radius: 4px;">
      <div class="d-flex align-center" style="gap: 8px">
        <v-icon color="red" size="24" class="pulse">mdi-record-circle</v-icon>
        <span class="text-body-2">{{ formatTime(recordingTime) }}</span>
        <v-btn
          color="error"
          icon="mdi-stop"
          @click="stopRecording"
        />
      </div>
    </div>

    <div v-if="hasAudio && !isRecording" class="audio-player mb-2">
      <audio
        ref="audioPlayer"
        :src="audioUrl"
        controls
        style="width: 100%"
      />
      <div class="d-flex align-center mt-2" style="gap: 8px">
        <v-btn
          color="primary"
          prepend-icon="mdi-replay"
          size="small"
          @click="clearRecording"
        >
          Re-record
        </v-btn>
        <v-chip
          :label="`${formatDuration(duration)}`"
          color="info"
          size="small"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AudioRecorder",
  props: {
    modelValue: {
      type: [Blob, String, null],
      default: null,
    },
    maxDuration: {
      type: Number,
      default: 300, // 5 minutes default
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      isRecording: false,
      mediaRecorder: null,
      audioChunks: [],
      audioBlob: null,
      audioUrl: null,
      recordingTime: 0,
      recordingTimer: null,
      duration: 0,
      canRecord: false,
    };
  },
  computed: {
    hasAudio() {
      return (this.audioBlob && this.audioBlob.size > 0) || (this.audioUrl && this.audioUrl.length > 0);
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (newVal && typeof newVal === "string") {
          // Saved URL from server (e.g. Azure Blob)
          this.audioUrl = newVal;
          this.audioBlob = null;
          this.$nextTick(() => this.calculateDuration());
        } else if (newVal instanceof Blob) {
          this.audioBlob = newVal;
          this.audioUrl = URL.createObjectURL(newVal);
          this.$nextTick(() => this.calculateDuration());
        }
      },
    },
  },
  mounted() {
    this.checkRecordingSupport();
  },
  beforeUnmount() {
    this.cleanup();
  },
  methods: {
    checkRecordingSupport() {
      this.canRecord =
        navigator.mediaDevices &&
        navigator.mediaDevices.getUserMedia &&
        window.MediaRecorder;
    },
    async startRecording() {
      if (!this.canRecord) {
        this.$toast?.error?.("Audio recording not supported");
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.mediaRecorder = new MediaRecorder(stream);
        this.audioChunks = [];
        this.recordingTime = 0;

        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data);
          }
        };

        this.mediaRecorder.onstop = () => {
          this.audioBlob = new Blob(this.audioChunks, { type: "audio/webm" });
          this.audioUrl = URL.createObjectURL(this.audioBlob);
          this.calculateDuration();
          this.$emit("update:modelValue", this.audioBlob);
          stream.getTracks().forEach((track) => track.stop());
        };

        this.mediaRecorder.start();
        this.isRecording = true;

        // Start timer
        this.recordingTimer = setInterval(() => {
          this.recordingTime++;
          if (this.recordingTime >= this.maxDuration) {
            this.stopRecording();
          }
        }, 1000);
      } catch (err) {
        this.$toast?.error?.("Failed to start recording: " + err.message);
      }
    },
    stopRecording() {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop();
        this.isRecording = false;
        if (this.recordingTimer) {
          clearInterval(this.recordingTimer);
          this.recordingTimer = null;
        }
      }
    },
    clearRecording() {
      this.cleanup();
      this.audioBlob = null;
      this.audioUrl = null;
      this.recordingTime = 0;
      this.duration = 0;
      this.$emit("update:modelValue", null);
    },
    calculateDuration() {
      if (this.$refs.audioPlayer) {
        this.$refs.audioPlayer.addEventListener("loadedmetadata", () => {
          this.duration = this.$refs.audioPlayer.duration || 0;
        });
        this.$refs.audioPlayer.load();
      }
    },
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    },
    formatDuration(seconds) {
      if (!seconds) return "0:00";
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    },
    cleanup() {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop();
      }
      if (this.recordingTimer) {
        clearInterval(this.recordingTimer);
        this.recordingTimer = null;
      }
      if (this.audioUrl && this.audioUrl.startsWith("blob:")) {
        URL.revokeObjectURL(this.audioUrl);
      }
    },
  },
};
</script>

<style scoped>
.audio-recorder {
  width: 100%;
}

.pulse {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>

